/*
 * Copyright 2023 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TimeTicket } from '@yorkie-js/sdk/src/document/time/ticket';
import { ChangeContext } from '@yorkie-js/sdk/src/document/change/context';
import {
  CRDTTree,
  CRDTTreeNodeID,
  CRDTTreeNode,
  CRDTTreePos,
} from '@yorkie-js/sdk/src/document/crdt/tree';

import {
  IndexTree,
  DefaultRootType,
  DefaultTextType,
} from '@yorkie-js/sdk/src/util/index_tree';
import { TreeEditOperation } from '@yorkie-js/sdk/src/document/operation/tree_edit_operation';
import { isEmpty, stringifyObjectValues } from '@yorkie-js/sdk/src/util/object';
import { RHT } from '../crdt/rht';
import { TreeStyleOperation } from '../operation/tree_style_operation';
import type {
  ElementNode,
  TextNode,
  TreeNode,
  TreeChange,
  TreeChangeType,
  TreePosStructRange,
  CRDTTreeNodeIDStruct,
} from '@yorkie-js/sdk/src/document/crdt/tree';
import type * as Devtools from '@yorkie-js/sdk/src/devtools/types';
import { Code, YorkieError } from '@yorkie-js/sdk/src/util/error';
import { ensureNoLoneSurrogate } from '@yorkie-js/sdk/src/document/json/strings';

/**
 * NOTE(hackerwins): In normal case, we should define the following types in
 * json package, because they are directly used by the user. However, we define
 * them in crdt package to avoid circular dependency between json and crdt.
 */
export {
  TreeNode,
  ElementNode,
  TextNode,
  TreeChange,
  TreeChangeType,
  TreePosStructRange,
  CRDTTreeNodeIDStruct,
};

/**
 * `buildDescendants` builds descendants of the given tree node.
 */
function buildDescendants(
  treeNode: TreeNode,
  parent: CRDTTreeNode,
  context: ChangeContext,
) {
  const { type } = treeNode;
  const ticket = context.issueTimeTicket();

  if (type === DefaultTextType) {
    validateTextNode(treeNode as TextNode);
    const { value } = treeNode as TextNode;
    const textNode = CRDTTreeNode.create(
      CRDTTreeNodeID.of(ticket, 0),
      type,
      value,
    );

    parent.append(textNode);
  } else {
    const { children = [] } = treeNode as ElementNode;
    const { attributes } = treeNode as ElementNode;
    let attrs;

    if (typeof attributes === 'object' && !isEmpty(attributes)) {
      const stringifiedAttributes = stringifyObjectValues(attributes);
      attrs = new RHT();

      for (const [key, value] of Object.entries(stringifiedAttributes)) {
        attrs.set(key, value, ticket);
      }
    }
    const elementNode = CRDTTreeNode.create(
      CRDTTreeNodeID.of(ticket, 0),
      type,
      undefined,
      attrs,
    );

    parent.append(elementNode);

    for (const child of children) {
      buildDescendants(child, elementNode, context);
    }
  }
}

/**
 * createCRDTTreeNode returns CRDTTreeNode by given TreeNode.
 */
function createCRDTTreeNode(context: ChangeContext, content: TreeNode) {
  const { type } = content;
  const ticket = context.issueTimeTicket();

  let root;
  if (content.type === DefaultTextType) {
    const { value } = content as TextNode;
    root = CRDTTreeNode.create(CRDTTreeNodeID.of(ticket, 0), type, value);
  } else if (content) {
    const { children = [] } = content as ElementNode;
    const { attributes } = content as ElementNode;
    let attrs;

    if (typeof attributes === 'object' && !isEmpty(attributes)) {
      const stringifiedAttributes = stringifyObjectValues(attributes);
      attrs = new RHT();

      for (const [key, value] of Object.entries(stringifiedAttributes)) {
        attrs.set(key, value, ticket);
      }
    }

    root = CRDTTreeNode.create(
      CRDTTreeNodeID.of(context.issueTimeTicket(), 0),
      type,
      undefined,
      attrs,
    );

    for (const child of children) {
      buildDescendants(child, root, context);
    }
  }

  return root;
}

/**
 * `comparePath` compares two paths of equal length in document order.
 */
function comparePath(a: Array<number>, b: Array<number>): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return a[i] - b[i];
    }
  }
  return 0;
}

/**
 * `validateTextNode` ensures that a text node has a non-empty string value
 * that does not carry a lone half of a UTF-16 surrogate pair. The index guard
 * in `CRDTTree.findPos` refuses to split a pair; this is the other half of the
 * same contract, since a stored lone half pairs with the code unit next to it
 * and makes the index at that seam permanently unusable.
 */
function validateTextNode(textNode: TextNode): boolean {
  if (!textNode.value.length) {
    throw new YorkieError(
      Code.ErrInvalidArgument,
      'text node cannot have empty value',
    );
  }
  ensureNoLoneSurrogate(textNode.value);

  return true;
}

/**
 * `validateTreeNodes` ensures that treeNodes consists of only one type.
 */
function validateTreeNodes(treeNodes: Array<TreeNode>): boolean {
  if (!treeNodes.length) {
    return true;
  }

  const firstTreeNodeType = treeNodes[0].type;
  if (firstTreeNodeType === DefaultTextType) {
    for (const treeNode of treeNodes) {
      const { type } = treeNode;
      if (type !== DefaultTextType) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'element node and text node cannot be passed together',
        );
      }
      validateTextNode(treeNode as TextNode);
    }
  } else {
    for (const treeNode of treeNodes) {
      const { type } = treeNode;
      if (type === DefaultTextType) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'element node and text node cannot be passed together',
        );
      }
    }
  }

  return true;
}

/**
 * `Tree` is a CRDT-based tree structure that is used to represent the document
 * tree of text-based editor such as ProseMirror.
 */
export class Tree {
  private initialRoot?: ElementNode;
  private context?: ChangeContext;
  private tree?: CRDTTree;

  constructor(initialRoot?: ElementNode) {
    this.initialRoot = initialRoot;
  }

  /**
   * `initialize` initialize this tree with context and internal tree.
   */
  public initialize(context: ChangeContext, tree: CRDTTree): void {
    this.context = context;
    this.tree = tree;
  }

  /**
   * `getID` returns the ID of this tree.
   */
  public getID(): TimeTicket {
    return this.tree!.getID();
  }

  /**
   * `buildRoot` builds the root of this tree with the given initial root
   * which set by the user.
   */
  public buildRoot(context: ChangeContext): CRDTTreeNode {
    if (!this.initialRoot) {
      return CRDTTreeNode.create(
        CRDTTreeNodeID.of(context.issueTimeTicket(), 0),
        DefaultRootType,
      );
    }

    // TODO(hackerwins): Need to use the ticket of operation of creating tree.
    const root = CRDTTreeNode.create(
      CRDTTreeNodeID.of(context.issueTimeTicket(), 0),
      this.initialRoot.type,
    );

    for (const child of this.initialRoot.children) {
      buildDescendants(child, root, context);
    }

    return root;
  }

  /**
   * `getSize` returns the size of this tree.
   */
  public getSize(): number {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.getSize();
  }

  /**
   * `getNodeSize` returns the node size of this tree.
   */
  public getNodeSize(): number {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.getNodeSize();
  }

  /**
   * `getIndexTree` returns the index tree of this tree.
   */
  public getIndexTree(): IndexTree<CRDTTreeNode> {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.getIndexTree();
  }

  /**
   * `splitByPath` splits the tree by the given path.
   * A path whose text offset falls inside a UTF-16 surrogate pair throws
   * `ErrInvalidArgument`.
   */
  public splitByPath(path: Array<number>) {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    if (!path.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path should not be empty',
      );
    }

    // A split divides the node holding the position into two siblings, so it
    // needs a parent to hold them. The root has none, and `edit` takes the
    // position anyway and pushes an operation that changes nothing.
    const treePos = this.tree.pathToTreePos(path);
    const target = treePos.node.isText ? treePos.node.parent! : treePos.node;

    if (!target.parent) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'the root node cannot be split',
      );
    }

    // Split through `edit`'s split level rather than lowering to a delete plus
    // an insert of a copied node. The copy made each replica insert its own
    // node, so two replicas splitting the same position kept both copies.
    const pos = this.tree.pathToPos(path);

    this.editInternal(pos, pos, [], 1);
  }

  /**
   * `mergeByPath` merges the tree by the given path.
   */
  public mergeByPath(path: Array<number>) {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    if (!path.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path should not be empty',
      );
    }

    const treePos = this.tree.pathToTreePos(path);

    if (treePos.node.isText) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'text node cannot be merged',
      );
    }

    const { node: parentNode, offset } = treePos;
    const leftSibling = parentNode.children[offset - 1];

    if (!leftSibling) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'the first child cannot be merged',
      );
    }

    // Merge by deleting the empty range across the boundary, rather than
    // lowering to a delete of the node plus an insert of its children copied
    // into the left sibling. The copy made each replica insert its own
    // children, so two replicas merging the same boundary kept both copies.
    // Deleting the boundary tokens re-inserts nothing and is idempotent.
    const parentPath = path.slice(0, -1);
    const fromPath = [
      ...parentPath,
      offset - 1,
      leftSibling.hasTextChild()
        ? leftSibling.getChildrenText().length
        : leftSibling.children.length,
    ];
    const fromPos = this.tree.pathToPos(fromPath);
    const toPos = this.tree.pathToPos([...path, 0]);

    this.editInternal(fromPos, toPos, []);
  }

  /**
   * `styleByPath` sets the attributes to the element at the given
   * path.
   */
  public styleByPath(
    path: Array<number>,
    attributes: { [key: string]: any },
  ): void;
  /**
   * `styleByPath` sets the attributes to the elements in the given
   * path range.
   */
  public styleByPath(
    fromPath: Array<number>,
    toPath: Array<number>,
    attributes: { [key: string]: any },
  ): void;
  /**
   * `styleByPath` sets the attributes to the elements of the given
   * path. When called with two paths, it styles the range between
   * them.
   * A path whose text offset falls inside a UTF-16 surrogate pair throws
   * `ErrInvalidArgument`.
   */
  public styleByPath(
    fromPathOrPath: Array<number>,
    toPathOrAttrs: Array<number> | { [key: string]: any },
    maybeAttrs?: { [key: string]: any },
  ): void {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    let fromPos: CRDTTreePos;
    let toPos: CRDTTreePos;
    let attributes: { [key: string]: any };

    if (Array.isArray(toPathOrAttrs)) {
      // Range mode: styleByPath(fromPath, toPath, attrs)
      const fromPath = fromPathOrPath;
      const toPath = toPathOrAttrs;
      attributes = maybeAttrs!;

      if (fromPath.length !== toPath.length) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'path length should be equal',
        );
      }
      if (!fromPath.length || !toPath.length) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'path should not be empty',
        );
      }
      // Same contract as the index form: a range given backwards is a caller
      // error, not an empty range.
      if (comparePath(fromPath, toPath) > 0) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'from should be less than or equal to to',
        );
      }

      fromPos = this.tree.pathToPos(fromPath);
      toPos = this.tree.pathToPos(toPath);
    } else {
      // Single-path mode: styleByPath(path, attrs)
      const path = fromPathOrPath;
      attributes = toPathOrAttrs;

      if (!path.length) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'path should not be empty',
        );
      }

      [fromPos, toPos] = this.tree.pathToPosRange(path);
    }

    const ticket = this.context.issueTimeTicket();
    const attrs = attributes ? stringifyObjectValues(attributes) : undefined;

    const [pairs, , size] = this.tree!.style([fromPos, toPos], attrs, ticket);

    this.context!.acc(size.live);
    this.context!.accGC(size.gc);

    for (const pair of pairs) {
      this.context!.registerGCPair(pair);
    }

    this.context.push(
      TreeStyleOperation.create(
        this.tree.getCreatedAt(),
        fromPos,
        toPos,
        attrs ? new Map(Object.entries(attrs)) : new Map(),
        ticket,
      ),
    );
  }

  /**
   * `style` sets the attributes to the elements of the given range.
   * An index inside a UTF-16 surrogate pair throws `ErrInvalidArgument`.
   */
  public style(
    fromIdx: number,
    toIdx: number,
    attributes: { [key: string]: any },
  ) {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    if (fromIdx > toIdx) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'from should be less than or equal to to',
      );
    }

    const fromPos = this.tree.findPos(fromIdx);
    const toPos = this.tree.findPos(toIdx);
    const ticket = this.context.issueTimeTicket();
    const attrs = attributes ? stringifyObjectValues(attributes) : undefined;

    const [pairs, , size] = this.tree!.style([fromPos, toPos], attrs, ticket);

    this.context!.acc(size.live);
    this.context!.accGC(size.gc);

    for (const pair of pairs) {
      this.context!.registerGCPair(pair);
    }

    this.context.push(
      TreeStyleOperation.create(
        this.tree.getCreatedAt(),
        fromPos,
        toPos,
        attrs ? new Map(Object.entries(attrs)) : new Map(),
        ticket,
      ),
    );
  }

  /**
   * `removeStyle` removes the attributes to the elements of the given range.
   * An index inside a UTF-16 surrogate pair throws `ErrInvalidArgument`.
   */
  public removeStyle(
    fromIdx: number,
    toIdx: number,
    attributesToRemove: Array<string>,
  ) {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    if (fromIdx > toIdx) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'from should be less than or equal to to',
      );
    }

    const fromPos = this.tree.findPos(fromIdx);
    const toPos = this.tree.findPos(toIdx);
    const ticket = this.context.issueTimeTicket();

    const [pairs, , size] = this.tree!.removeStyle(
      [fromPos, toPos],
      attributesToRemove,
      ticket,
    );

    this.context!.acc(size.live);
    this.context!.accGC(size.gc);

    for (const pair of pairs) {
      this.context!.registerGCPair(pair);
    }

    this.context.push(
      TreeStyleOperation.createTreeRemoveStyleOperation(
        this.tree.getCreatedAt(),
        fromPos,
        toPos,
        attributesToRemove,
        ticket,
      ),
    );
  }

  /**
   * `removeStyleByPath` removes the attributes of the elements in
   * the given path range.
   * A path whose text offset falls inside a UTF-16 surrogate pair throws
   * `ErrInvalidArgument`.
   */
  public removeStyleByPath(
    fromPath: Array<number>,
    toPath: Array<number>,
    attributesToRemove: Array<string>,
  ): void {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    if (fromPath.length !== toPath.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path length should be equal',
      );
    }
    if (!fromPath.length || !toPath.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path should not be empty',
      );
    }

    // Same contract as the index form: a range given backwards is a caller
    // error, not an empty range.
    if (comparePath(fromPath, toPath) > 0) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'from should be less than or equal to to',
      );
    }

    const fromPos = this.tree.pathToPos(fromPath);
    const toPos = this.tree.pathToPos(toPath);
    const ticket = this.context.issueTimeTicket();

    const [pairs, , size] = this.tree!.removeStyle(
      [fromPos, toPos],
      attributesToRemove,
      ticket,
    );

    this.context!.acc(size.live);
    this.context!.accGC(size.gc);

    for (const pair of pairs) {
      this.context!.registerGCPair(pair);
    }

    this.context.push(
      TreeStyleOperation.createTreeRemoveStyleOperation(
        this.tree.getCreatedAt(),
        fromPos,
        toPos,
        attributesToRemove,
        ticket,
      ),
    );
  }

  private editInternal(
    fromPos: CRDTTreePos,
    toPos: CRDTTreePos,
    contents: Array<TreeNode>,
    splitLevel = 0,
  ): boolean {
    if (contents.length !== 0 && contents[0]) {
      validateTreeNodes(contents);
      if (contents[0].type !== DefaultTextType) {
        for (const content of contents) {
          const { children = [] } = content as ElementNode;
          validateTreeNodes(children);
        }
      }
    }

    const ticket = this.context!.getLastTimeTicket();
    let crdtNodes = new Array<CRDTTreeNode>();

    if (contents[0]?.type === DefaultTextType) {
      let compVal = '';
      for (const content of contents) {
        const { value } = content as TextNode;
        compVal += value;
      }
      crdtNodes.push(
        CRDTTreeNode.create(
          CRDTTreeNodeID.of(this.context!.issueTimeTicket(), 0),
          DefaultTextType,
          compVal,
        ),
      );
    } else {
      crdtNodes = contents
        .map((content) => content && createCRDTTreeNode(this.context!, content))
        .filter((a) => a) as Array<CRDTTreeNode>;
    }

    // Splitting an element creates nodes that need tickets. Record the ones
    // issued so the operation can carry them: every other replica then uses
    // them instead of reconstructing them from the operation, which it cannot
    // do correctly once content has descendants.
    const splitTickets: Array<TimeTicket> = [];
    const [, pairs, diff] = this.tree!.edit(
      [fromPos, toPos],
      crdtNodes.length
        ? crdtNodes.map((crdtNode) => crdtNode?.deepcopy())
        : undefined,
      splitLevel,
      ticket,
      () => {
        const issued = this.context!.issueTimeTicket();
        splitTickets.push(issued);
        return issued;
      },
    );

    this.context!.acc(diff);

    for (const pair of pairs) {
      this.context!.registerGCPair(pair);
    }

    const edit = TreeEditOperation.create(
      this.tree!.getCreatedAt(),
      fromPos,
      toPos,
      crdtNodes.length ? crdtNodes : undefined,
      splitLevel,
      ticket,
    );
    edit.setSplitTickets(splitTickets);
    this.context!.push(edit);

    return true;
  }

  /**
   * `editByPath` edits this tree with the given node and path.
   * A path whose text offset falls inside a UTF-16 surrogate pair throws
   * `ErrInvalidArgument`.
   */
  public editByPath(
    fromPath: Array<number>,
    toPath: Array<number>,
    content?: TreeNode,
    splitLevel = 0,
  ): boolean {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }
    if (fromPath.length !== toPath.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path length should be equal',
      );
    }
    if (!fromPath.length || !toPath.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path should not be empty',
      );
    }

    const fromPos = this.tree.pathToPos(fromPath);
    const toPos = this.tree.pathToPos(toPath);

    return this.editInternal(
      fromPos,
      toPos,
      content ? [content] : [],
      splitLevel,
    );
  }

  /**
   * `editBulkByPath` edits this tree with the given node and path.
   * A path whose text offset falls inside a UTF-16 surrogate pair throws
   * `ErrInvalidArgument`.
   */
  public editBulkByPath(
    fromPath: Array<number>,
    toPath: Array<number>,
    contents: Array<TreeNode>,
    splitLevel = 0,
  ): boolean {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }
    if (fromPath.length !== toPath.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path length should be equal',
      );
    }
    if (!fromPath.length || !toPath.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'path should not be empty',
      );
    }

    const fromPos = this.tree.pathToPos(fromPath);
    const toPos = this.tree.pathToPos(toPath);

    return this.editInternal(fromPos, toPos, contents, splitLevel);
  }

  /**
   * `edit` edits this tree with the given nodes.
   * An index inside a UTF-16 surrogate pair throws `ErrInvalidArgument`.
   */
  public edit(
    fromIdx: number,
    toIdx: number,
    content?: TreeNode,
    splitLevel = 0,
  ): boolean {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }
    if (fromIdx > toIdx) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'from should be less than or equal to to',
      );
    }

    const fromPos = this.tree.findPos(fromIdx);
    const toPos = this.tree.findPos(toIdx);

    return this.editInternal(
      fromPos,
      toPos,
      content ? [content] : [],
      splitLevel,
    );
  }

  /**
   * `editBulk` edits this tree with the given nodes.
   * An index inside a UTF-16 surrogate pair throws `ErrInvalidArgument`.
   */
  public editBulk(
    fromIdx: number,
    toIdx: number,
    contents: Array<TreeNode>,
    splitLevel = 0,
  ): boolean {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }
    if (fromIdx > toIdx) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'from should be less than or equal to to',
      );
    }

    const fromPos = this.tree.findPos(fromIdx);
    const toPos = this.tree.findPos(toIdx);

    return this.editInternal(fromPos, toPos, contents, splitLevel);
  }

  /**
   * `toXML` returns the XML string of this tree.
   */
  public toXML(): string {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.toXML();
  }

  /**
   * `toJSON` returns the JSON string of this tree.
   */
  public toJSON(): string {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.toJSON();
  }

  /**
   * `toJSForTest` returns value with meta data for testing.
   */
  public toJSForTest(): Devtools.JSONElement {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.toJSForTest();
  }

  /**
   * `toJSInfoForTest` returns detailed TreeNode information for use in Devtools.
   */
  public toJSInfoForTest(): Devtools.TreeNodeInfo {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.toJSInfoForTest();
  }

  /**
   * `getRootTreeNode` returns TreeNode of this tree.
   */
  public getRootTreeNode() {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.getRootTreeNode();
  }

  /**
   * `indexToPath` returns the path of the given index.
   */
  public indexToPath(index: number): Array<number> {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.indexToPath(index);
  }

  /**
   * `pathToIndex` returns the index of given path.
   */
  public pathToIndex(path: Array<number>): number {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.pathToIndex(path);
  }

  /**
   * `pathRangeToPosRange` converts the path range into the position range.
   */
  pathRangeToPosRange(
    range: [Array<number>, Array<number>],
  ): TreePosStructRange {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    const indexRange: [number, number] = [
      this.tree.pathToIndex(range[0]),
      this.tree.pathToIndex(range[1]),
    ];
    const posRange = this.tree.indexRangeToPosRange(indexRange);
    return [posRange[0].toStruct(), posRange[1].toStruct()];
  }

  /**
   * `indexRangeToPosRange` converts the index range into the position range.
   */
  indexRangeToPosRange(range: [number, number]): TreePosStructRange {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    return this.tree.indexRangeToPosStructRange(range);
  }

  /**
   * `posRangeToIndexRange` converts the position range into the index range.
   */
  posRangeToIndexRange(range: TreePosStructRange): [number, number] {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    const posRange: [CRDTTreePos, CRDTTreePos] = [
      CRDTTreePos.fromStruct(range[0]),
      CRDTTreePos.fromStruct(range[1]),
    ];

    const indexRange = this.tree.posRangeToIndexRange(posRange);
    this.registerPendingGCPairs();
    return indexRange;
  }

  /**
   * `posRangeToPathRange` converts the position range into the path range.
   */
  posRangeToPathRange(
    range: TreePosStructRange,
  ): [Array<number>, Array<number>] {
    if (!this.context || !this.tree) {
      throw new YorkieError(
        Code.ErrNotInitialized,
        'Tree is not initialized yet',
      );
    }

    const posRange: [CRDTTreePos, CRDTTreePos] = [
      CRDTTreePos.fromStruct(range[0]),
      CRDTTreePos.fromStruct(range[1]),
    ];

    const pathRange = this.tree.posRangeToPathRange(posRange);
    this.registerPendingGCPairs();
    return pathRange;
  }

  /**
   * `registerPendingGCPairs` registers with the root any GC pairs the tree
   * buffered while resolving a position. `posRangeToIndexRange` and
   * `posRangeToPathRange` split text nodes to locate a position; when the
   * position lands inside a tombstoned node the split produces a
   * born-removed piece. Unlike edit/style/removeStyle, these read-path
   * conversions emit no operation, so the buffered pairs would otherwise
   * never reach the root and the piece would leak (invisible to GC).
   */
  private registerPendingGCPairs(): void {
    for (const pair of this.tree!.drainPendingGCPairs()) {
      this.context!.registerGCPair(pair);
    }
  }
}
